<template>
  <v-card border>
    <v-card-text class="py-4">
      <div class="d-flex align-center justify-space-between">
        <div class="d-flex align-center ga-3">
          <v-btn
            :icon="isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"
            color="grey-lighten-4"
            density="comfortable"
            max-height="28px"
            max-width="28px"
            @click="isExpanded = !isExpanded"></v-btn>

          <b>{{ issue.topic }}</b>
        </div>

        <div class="d-flex align-center ga-2">
          <v-chip
            :style="getIssueStatusChipStyle(issue.statusId)"
            border
            density="comfortable"
            rounded="lg"
            size="small"
            variant="flat">
            <span class="font-weight-bold">
              {{ issue.status?.name || '---' }}
            </span>
          </v-chip>
          <b-action-menu
            v-if="canUpdateIncidentIssue"
            :actions="[
              {
                title: 'Редагувати',
                action: () => incidentIssueUpdate(issue),
                icon: 'mdi-pencil',
                iconProps: {
                  color: 'blue-darken-3',
                },
              },
              {
                title: 'Змінити статус',
                action: () => incidentIssueSetStatus(issue),
                icon: 'mdi-arrow-u-right-bottom',
                iconProps: {
                  color: 'warning',
                },
              },
              {
                title: 'Видалити',
                action: () => incidentIssueDelete(issue.id),
                icon: 'mdi-delete-forever',
                listItemProps: {
                  class: 'text-error',
                },
              },
            ]"
            @click.stop>
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                color="white"
                border
                icon
                max-width="28px"
                max-height="28px">
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
          </b-action-menu>
        </div>
      </div>
    </v-card-text>

    <transition name="expand">
      <div v-if="isExpanded">
        <v-divider></v-divider>

        <v-card-text>
          <div class="label">Опис недоліку</div>
          <div class="mb-1">{{ issue.description }}</div>
          <div class="text-disabled font-size-13">
            Зафіксовано: {{ new CustomDate(issue.created_at).toString() }}
          </div>

          <v-divider class="my-2"></v-divider>

          <div class="d-flex align-center justify-space-between ga-3 mb-2">
            <div class="label mb-0">Повʼязані завдання</div>

            <div
              v-if="canCreateTask"
              class="d-flex align-center ga-1 text-blue-darken-3 font-weight-bold font-size-12 cursor-pointer"
              @click="openIncidentIssueTaskDialog(issue)">
              <v-icon size="small">mdi-plus</v-icon>
              <span>Додати завдання</span>
            </div>
          </div>

          <v-data-table
            :headers="headers"
            :hide-default-footer="true"
            :items="issue.tasks || []"
            class="mb-3"
            color="red"
            density="compact"
            item-value="id"
            rounded="pill">
            <template #item.status="{ item }">
              <v-chip
                :style="getTaskStatusChipStyle(item.statusId)"
                border
                density="compact"
                rounded="lg"
                variant="flat">
                <span class="font-weight-bold">
                  {{ item.status?.name || '---' }}
                </span>
              </v-chip>
            </template>

            <template #item.task="{ item }">
              <span
                v-if="item.id"
                v-tooltip:bottom="item.status?.name"
                class="link"
                @click="
                  $router.push({
                    name: 'single-task',
                    params: { id: item.id },
                  })
                ">
                {{ item.topic }}
              </span>
              <span v-else>---</span>
            </template>

            <template #item.executor="{ item }">
              <span>{{
                getPersonName(item.executor, { initials: true }) || '---'
              }}</span>
            </template>

            <template #item.deadline="{ item }">
              <span>
                {{
                  item.deadline
                    ? new CustomDate(item.deadline).toString({
                        time: true,
                        format: 'locale-hybrid',
                      })
                    : '---'
                }}
              </span>
            </template>

            <template #no-data>
              <div class="text-disabled py-4">
                Повʼязане завдання ще не створено
              </div>
            </template>
          </v-data-table>
        </v-card-text>
      </div>
    </transition>
  </v-card>
</template>

<script setup lang="ts">
import { computed, PropType, ref } from 'vue'
import { BActionMenu } from 'best-modules/components'
import { CustomDate } from 'best-modules/utils'
import { getPersonName } from 'best-modules/utils/helpers'
import { useIncident } from '../../Incident'
import type { IncidentIssue } from '@/utils/types/incident'
import { incidentIssueTaskHeaders } from '@/utils/tableHeaders'
import { useCan } from '@/composables/useCan'

defineOptions({
  name: 'IssueItem',
})

const props = defineProps({
  issue: {
    type: Object as PropType<IncidentIssue>,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
})

const {
  canUpdateIncidentIssue,
  openIncidentIssueTaskDialog,
  incidentIssueUpdate,
  incidentIssueDelete,
  incidentIssueSetStatus,
} = useIncident()
const { can } = useCan()

const isExpanded = ref<boolean>(props.index === 0)
const headers = incidentIssueTaskHeaders
const canCreateTask = computed(() => can('tasks.create'))

const getIssueStatusColor = (statusId?: number | null) => {
  if (statusId === 2) return '#187a4d'
  if (statusId === 3) return '#b42318'

  return '#1565a9'
}

const getIssueStatusChipStyle = (statusId?: number | null) => {
  if (statusId === 2) {
    return {
      backgroundColor: '#e9f8f0',
      borderColor: '#9bdcbb',
      color: getIssueStatusColor(statusId),
    }
  }

  if (statusId === 3) {
    return {
      backgroundColor: '#fff1f1',
      borderColor: '#f1aaaa',
      color: getIssueStatusColor(statusId),
    }
  }

  return {
    backgroundColor: '#eaf4ff',
    borderColor: '#9bcaf5',
    color: getIssueStatusColor(statusId),
  }
}

const getTaskStatusChipStyle = (statusId?: number | null) => {
  if (statusId === 3) {
    return {
      backgroundColor: '#e9f8f0',
      borderColor: '#9bdcbb',
      color: '#187a4d',
    }
  }

  if (statusId === 4) {
    return {
      backgroundColor: '#fff1f1',
      borderColor: '#f1aaaa',
      color: '#b42318',
    }
  }

  if (statusId === 2) {
    return {
      backgroundColor: '#eaf4ff',
      borderColor: '#9bcaf5',
      color: '#1565a9',
    }
  }

  return {
    backgroundColor: '#fff8e8',
    borderColor: '#f4cf89',
    color: '#b66f00',
  }
}
</script>
